import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Clock, Users, Bookmark, BookmarkCheck, ExternalLink, Bell, BellOff, Loader2, Heart, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

function formatLongDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

export default function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function loadEvent() {
    try {
      const data = await api(`/events/${eventId}`);
      setEvent(data.event);
    } catch (err) {
      toast.error(err.message || 'Failed to load');
      navigate('/app/events');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  async function handleInterest(status) {
    try {
      await api(`/events/${eventId}/interest`, {
        method: 'POST',
        body: JSON.stringify({ event_id: eventId, status }),
      });
      toast.success(status === 'none' ? 'Removed' : `Marked as ${status}`);
      loadEvent();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  async function handleToggleSave() {
    if (saving) return;
    setSaving(true);
    try {
      if (event.is_saved) {
        await api(`/events/${eventId}/save`, { method: 'DELETE' });
        toast.success('Removed from saved');
      } else {
        await api(`/events/${eventId}/save`, { method: 'POST' });
        toast.success('Saved');
      }
      loadEvent();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleReminder() {
    try {
      if (event.has_reminder) {
        await api(`/events/${eventId}/reminder`, { method: 'DELETE' });
        toast.success('Reminder removed');
      } else {
        // Set reminder for 1 hour before event
        const remindAt = new Date(event.date + 'T' + (event.time || '18:00') + ':00').toISOString();
        await api(`/events/${eventId}/reminder`, {
          method: 'POST',
          body: JSON.stringify({ event_id: eventId, remind_at: remindAt }),
        });
        toast.success('Reminder set');
      }
      loadEvent();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>;
  if (!event) return null;

  return (
    <div className="max-w-4xl mx-auto pb-12" data-testid={`event-detail-${event.id}`}>
      {/* Hero */}
      <div className="relative aspect-[16/9] sm:aspect-[3/1] bg-[#FFE5DD] mb-6">
        {event.image_url ? (
          <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar className="w-16 h-16 text-[#C8553D]/30" />
          </div>
        )}
        <button
          onClick={() => navigate('/app/events')}
          data-testid="event-back-button"
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md"
        >
          <ArrowLeft className="w-5 h-5 text-[#2B2D42]" />
        </button>
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={handleToggleReminder}
            data-testid="event-reminder-button"
            className="w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md"
          >
            {event.has_reminder ? <Bell className="w-5 h-5 text-[#C8553D]" fill="currentColor" /> : <BellOff className="w-5 h-5 text-[#6D6875]" />}
          </button>
          <button
            onClick={handleToggleSave}
            data-testid="event-save-button"
            disabled={saving}
            className="w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md"
          >
            {event.is_saved ? <BookmarkCheck className="w-5 h-5 text-[#C8553D]" fill="currentColor" /> : <Bookmark className="w-5 h-5 text-[#6D6875]" />}
          </button>
        </div>
      </div>

      <div className="px-4 sm:px-6">
        <p className="text-xs font-bold uppercase tracking-wider text-[#C8553D]" data-testid="event-detail-category">
          {event.category}
        </p>
        <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2B2D42] mt-1" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="event-detail-title">
          {event.title}
        </h1>

        {/* Meta strip */}
        <div className="grid sm:grid-cols-2 gap-3 mt-5 mb-5">
          <div className="bg-white border border-[#F4F1DE] rounded-xl p-3 flex items-center gap-3">
            <Calendar className="w-5 h-5 text-[#C8553D] flex-shrink-0" />
            <div className="text-sm">
              <p className="font-bold text-[#2B2D42]" data-testid="event-detail-date">{formatLongDate(event.date)}</p>
              <p className="text-xs text-[#6D6875]">{event.time}{event.end_time ? ` – ${event.end_time}` : ''}</p>
            </div>
          </div>
          <div className="bg-white border border-[#F4F1DE] rounded-xl p-3 flex items-center gap-3">
            <MapPin className="w-5 h-5 text-[#C8553D] flex-shrink-0" />
            <div className="text-sm">
              <p className="font-bold text-[#2B2D42]" data-testid="event-detail-venue">{event.venue}</p>
              <p className="text-xs text-[#6D6875]">{event.address || event.city}</p>
            </div>
          </div>
        </div>

        {/* Going / Interested counts + RSVP buttons */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => handleInterest(event.user_status === 'going' ? 'none' : 'going')}
            data-testid="event-going-button"
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm transition-colors ${
              event.user_status === 'going'
                ? 'bg-[#2A9D8F] text-white'
                : 'bg-white border-2 border-[#2A9D8F] text-[#2A9D8F] hover:bg-[#2A9D8F]/10'
            }`}
          >
            <Check className="w-4 h-4" /> Going ({event.going_count || 0})
          </button>
          <button
            onClick={() => handleInterest(event.user_status === 'interested' ? 'none' : 'interested')}
            data-testid="event-interested-button"
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm transition-colors ${
              event.user_status === 'interested'
                ? 'bg-[#E07A5F] text-white'
                : 'bg-white border-2 border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10'
            }`}
          >
            <Heart className="w-4 h-4" /> Interested ({event.interested_count || 0})
          </button>
        </div>

        {/* Eventbrite link */}
        {event.eventbrite_url && (
          <a
            href={event.eventbrite_url}
            target="_blank"
            rel="noreferrer"
            data-testid="event-tickets-link"
            className="inline-flex items-center gap-2 bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-5 py-2.5 rounded-full text-sm mb-5"
          >
            <ExternalLink className="w-4 h-4" />
            Get Tickets
          </a>
        )}

        {/* Description */}
        <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-2">About this event</h2>
          <p className="text-[#2B2D42] leading-relaxed whitespace-pre-wrap" data-testid="event-detail-description">{event.description}</p>
          {event.organizer && (
            <p className="text-xs text-[#6D6875] mt-3">
              Organized by <span className="font-bold text-[#2B2D42]">{event.organizer}</span>
            </p>
          )}
        </section>

        {/* Attendees preview */}
        {event.attendee_previews?.length > 0 && (
          <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="event-attendees">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" /> Who's going
            </h2>
            <div className="flex -space-x-2">
              {event.attendee_previews.slice(0, 8).map((a) => (
                <div key={a.id} className="w-10 h-10 rounded-full border-2 border-white bg-[#FFE5DD] overflow-hidden" title={a.name}>
                  {a.photo ? (
                    <img src={a.photo} alt={a.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs font-bold text-[#C8553D]">
                      {a.name?.charAt(0)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Similar events */}
        {event.similar_events?.length > 0 && (
          <section data-testid="event-similar">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3">More events you might like</h2>
            <ul className="space-y-2">
              {event.similar_events.slice(0, 3).map((e) => (
                <li key={e.id}>
                  <a
                    href={`/app/events/${e.id}`}
                    className="flex items-center gap-3 p-3 bg-white border border-[#F4F1DE] rounded-xl hover:bg-[#FFE5DD]/30"
                  >
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#FFE5DD] flex-shrink-0">
                      {e.image_url ? (
                        <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />
                      ) : (
                        <Calendar className="w-6 h-6 text-[#C8553D]/40 m-auto mt-3" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-[#2B2D42] truncate">{e.title}</p>
                      <p className="text-xs text-[#6D6875]">{formatLongDate(e.date)}</p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
